import axios from "axios";
import { useEffect, useState } from "react";
import { baseURL } from "../Constants";

const url = `${baseURL}/PlayerBadge`;

// Bump the suffix whenever PlayerBadges gains a field, so already-cached (old-shape) payloads are
// discarded instead of being read back with missing arrays.
export const CACHE_KEY = 'player_badges_cache_v4';
export const CACHE_DURATION_MS = 30 * 60 * 1000; // 30 minutes

export interface PlayerBadges {
    playerImages: string[];
    topDonatorUsers: string[];
    verifiedContentUsers: string[];
    hanErHamUsers: string[];
    /**
     * Fantasy division winners keyed by season, e.g. `{ "32": [steamId, ...] }`.
     *
     * One entry per season on purpose: the profile has shown a separate trophy per season since
     * S27, and a player can win their division in more than one (MeSterLund took S31 and S32).
     */
    fantasyWinners: Record<string, string[]>;
}

/** Badge types that mark a fantasy division win, e.g. `fantasy_winner_s33`. */
const FANTASY_WINNER_TYPE = /^fantasy_winner_s(\d+)$/;

/** Badge types the backend accepts — mirrors PlayerBadgeDA.BadgeTypes. */
export const BADGE_TYPES = ['player_image', 'top_donator', 'verified_content', 'han_er_ham', 'fantasy_winner_s32', 'fantasy_winner_s33'] as const;

export type BadgeType = typeof BADGE_TYPES[number];

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

interface CachedBadges {
    data: PlayerBadges;
    timestamp: number;
}

interface ApiBadgesResponse {
    player_image: string[];
    top_donator: string[];
    verified_content: string[];
    han_er_ham: string[];
    /** Plus one `fantasy_winner_sNN` key per season the backend knows about. */
    [badgeType: string]: string[] | undefined;
}

/**
 * Transforms API response to frontend-friendly format
 */
const transformBadges = (apiBadges: ApiBadgesResponse): PlayerBadges => {
    return {
        playerImages: apiBadges.player_image || [],
        topDonatorUsers: apiBadges.top_donator || [],
        verifiedContentUsers: apiBadges.verified_content || [],
        hanErHamUsers: apiBadges.han_er_ham || [],
        // Collected by pattern rather than by name, so a new season needs no frontend change.
        fantasyWinners: Object.entries(apiBadges).reduce((acc, [type, ids]) => {
            const season = FANTASY_WINNER_TYPE.exec(type);
            if (season) acc[season[1]] = ids || [];
            return acc;
        }, {} as Record<string, string[]>)
    };
};

/**
 * Gets all badges with localStorage caching (30 min TTL)
 * Falls back to stale cache if API fails
 */
export const getAllBadges = async (): Promise<PlayerBadges> => {
    // Check localStorage cache first
    try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
            const { data, timestamp }: CachedBadges = JSON.parse(cached);
            if (Date.now() - timestamp < CACHE_DURATION_MS) {
                return data; // Return cached data if fresh
            }
        }
    } catch (e) {
        // If cache parsing fails, just fetch fresh
        console.warn('Failed to parse badges cache:', e);
    }

    // Fetch from API and update cache
    try {
        const response = await axios.get<ApiBadgesResponse>(url);
        const badges = transformBadges(response.data);

        localStorage.setItem(CACHE_KEY, JSON.stringify({
            data: badges,
            timestamp: Date.now()
        }));

        return badges;
    } catch (error) {
        console.error('Error fetching badges:', error);

        // If fetch fails but we have stale cache, return it
        try {
            const cached = localStorage.getItem(CACHE_KEY);
            if (cached) {
                const { data }: CachedBadges = JSON.parse(cached);
                return data;
            }
        } catch (e) {
            // Ignore
        }

        // Return empty badges as last resort
        return {
            playerImages: [],
            topDonatorUsers: [],
            verifiedContentUsers: [],
            hanErHamUsers: [],
            fantasyWinners: {}
        };
    }
};

/**
 * Checks if a player has a custom image
 */
export const hasPlayerImage = (steamId: string, badges: PlayerBadges): boolean => {
    return badges.playerImages.includes(steamId);
};

/**
 * Checks if a player is a top donator
 */
export const isTopDonator = (steamId: string, badges: PlayerBadges): boolean => {
    return badges.topDonatorUsers.includes(steamId);
};

/**
 * Checks if a player is a verified content creator
 */
export const isVerifiedContent = (steamId: string, badges: PlayerBadges): boolean => {
    return badges.verifiedContentUsers.includes(steamId);
};

/**
 * Checks if a player has the "Han er ham" badge
 */
export const isHanErHam = (steamId: string, badges: PlayerBadges): boolean => {
    return (badges.hanErHamUsers ?? []).includes(steamId);
};

/**
 * Every season this player won their Fantasy division, oldest first.
 *
 * One trophy per season, which is how the profile has shown them since S27 — so a two-time
 * winner gets two. Seasons 27–31 predate the badge table and still live in `Constants.tsx`.
 */
export const fantasyWinnerSeasons = (steamId: string, badges: PlayerBadges): number[] => {
    return Object.entries(badges.fantasyWinners ?? {})
        .filter(([, ids]) => ids.includes(steamId))
        .map(([season]) => Number(season))
        .sort((a, b) => a - b);
};

/**
 * Clears the badges cache (useful after admin updates).
 *
 * Drops the in-memory copy as well as localStorage, then re-arms the shared fetch. Clearing only
 * localStorage would leave `moduleBadges` holding the pre-edit set for the rest of the SPA session,
 * so an admin who grants a badge and then navigates to that player's profile would not see it.
 */
export const clearBadgesCache = (): void => {
    localStorage.removeItem(CACHE_KEY);
    moduleBadges = null;
    badgesPromise = getAllBadges().then((b) => {
        moduleBadges = b;
        return b;
    });
};

// ---------------------------------------------------------------------------
// Shared page-wide badge state
//
// localStorage is read synchronously at module load — no API call, no async. That covers 99% of
// visits (the cache is valid for 30 min); only a cold cache needs the single background fetch
// below. Every consumer shares this one result, so a page full of PlayerImages costs one request.
// ---------------------------------------------------------------------------
let moduleBadges: PlayerBadges | null = null;

try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) {
        const { data, timestamp }: CachedBadges = JSON.parse(raw);
        if (Date.now() - timestamp < CACHE_DURATION_MS) {
            moduleBadges = data;
        }
    }
} catch {
    // Ignore parse errors
}

let badgesPromise: Promise<PlayerBadges> | null = null;
if (!moduleBadges) {
    badgesPromise = getAllBadges().then((b) => {
        moduleBadges = b;
        return b;
    });
}

/**
 * Badges for the current page. Resolves synchronously on a warm cache (so badges never flicker in),
 * otherwise returns null until the one shared background fetch lands.
 */
export const useBadges = (): PlayerBadges | null => {
    const [badges, setBadges] = useState<PlayerBadges | null>(moduleBadges);

    useEffect(() => {
        if (!badges && badgesPromise) {
            badgesPromise.then(setBadges);
        }
    }, [badges]);

    return badges;
};

/**
 * Grants a badge (admin only). Throws with a human-readable message on failure so the caller can
 * surface it directly — notably the 409 raised when the player has never signed in, which would
 * otherwise look like a silent no-op.
 */
export const addBadge = async (steamId: string, badgeType: BadgeType): Promise<void> => {
    try {
        await axios.post(url, { steamId, badgeType }, { headers: authHeader() });
    } catch (error) {
        throw new Error(badgeErrorMessage(error, 'add'));
    }
    clearBadgesCache();
};

/**
 * Revokes a badge (admin only).
 */
export const removeBadge = async (steamId: string, badgeType: BadgeType): Promise<void> => {
    try {
        await axios.delete(`${url}/${steamId}/${badgeType}`, { headers: authHeader() });
    } catch (error) {
        throw new Error(badgeErrorMessage(error, 'remove'));
    }
    clearBadgesCache();
};

const badgeErrorMessage = (error: unknown, action: 'add' | 'remove'): string => {
    if (axios.isAxiosError(error)) {
        const data = error.response?.data as { message?: string; Message?: string } | undefined;
        const serverMessage = data?.message ?? data?.Message;
        if (serverMessage) return serverMessage;
        if (error.response?.status === 401) return 'Admin access required — try signing in again.';
    }
    return `Could not ${action} badge.`;
};

/**
 * Gets badges for a specific player from the API
 */
export const getBadgesForPlayer = async (steamId: string): Promise<{
    steamId: string;
    badges: string[];
    hasPlayerImage: boolean;
    isTopDonator: boolean;
    isVerifiedContent: boolean;
} | null> => {
    try {
        const response = await axios.get(`${url}/player/${steamId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching player badges:', error);
        return null;
    }
};
